/**
 * COS 资源客户端导出主入口
 *
 * - `html`：fetch 文本 → 可选注水印 → 下载
 * - `zip`：fetch blob → 原样下载（不处理水印）
 * - `pdf`：隐藏 iframe 渲染 HTML → html2canvas → jsPDF（可选水印）
 *
 * @module @home/utils/htmlExport/downloadCosResource
 */

import html2canvas from 'html2canvas';
import { jsPDF as JsPDF } from 'jspdf';
import { downloadFile } from '@home/utils/file';
import { resolveCosFetchUrl } from './cosProxy';
import {
  nextAnimationFrame,
  resolveContentRoot,
  throwIfAborted,
  waitForIframeLoad,
  waitForImages,
} from './helpers';
import {
  applyWatermarkToCanvas,
  injectWatermarkIntoHtml,
  resolveWatermarkDataUrl,
} from './watermark';
import type { IDownloadCosResourceOptions, ICosExportPdfOptions } from './types';

const A4_W_MM = 210;
const A4_H_MM = 297;

const DEFAULT_PDF: Required<
Pick<
ICosExportPdfOptions,
| 'viewportWidth'
| 'scale'
| 'pageFormat'
| 'jpegQuality'
| 'iframeLoadTimeoutMs'
| 'imageLoadTimeoutMs'
>
> = {
  viewportWidth: 1280,
  scale: 2,
  pageFormat: 'a4',
  jpegQuality: 0.98,
  iframeLoadTimeoutMs: 30000,
  imageLoadTimeoutMs: 15000,
};

/**
 * 从 COS（或任意可访问 URL）导出并下载资源
 *
 * @param options - 见 {@link IDownloadCosResourceOptions}
 * @throws 网络失败、iframe 超时、AbortError、canvas 不可用等
 *
 * @example HTML
 * ```ts
 * await downloadCosResource({
 *   url,
 *   cosType: 'agentCos',
 *   format: 'html',
 *   fileName: 'gallery.html',
 * });
 * ```
 *
 * @example ZIP
 * ```ts
 * await downloadCosResource({
 *   url,
 *   cosType: 'agentCos',
 *   format: 'zip',
 *   fileName: 'skill.zip',
 * });
 * ```
 *
 * @example PDF（HTML 页面截图）
 * ```ts
 * await downloadCosResource({
 *   url,
 *   cosType: 'dashboardAnomalyCos',
 *   format: 'pdf',
 *   fileName: 'report.pdf',
 *   watermark: { text: 'u@DataBrain' },
 *   pdf: { pageFormat: 'a4', viewportWidth: 1280 },
 * });
 * ```
 */
export async function downloadCosResource(
  options: IDownloadCosResourceOptions,
): Promise<void> {
  const {
    url,
    cosType,
    useProxy,
    format,
    fileName,
    watermark,
    pdf,
    signal,
  } = options;

  if (!url) {
    throw new Error('[htmlExport] url is required');
  }
  if (!fileName) {
    throw new Error('[htmlExport] fileName is required');
  }

  throwIfAborted(signal);

  const fetchUrl = resolveCosFetchUrl(url, cosType, useProxy);
  if (!fetchUrl) {
    throw new Error('[htmlExport] failed to resolve fetch url');
  }

  if (format === 'html') {
    await downloadAsHtml(fetchUrl, fileName, watermark, signal);
    return;
  }

  if (format === 'zip') {
    await downloadAsBlob(fetchUrl, fileName, 'application/zip', signal);
    return;
  }

  if (format === 'pdf') {
    await downloadAsPdf(fetchUrl, fileName, watermark, pdf, signal);
    return;
  }

  throw new Error(`[htmlExport] unsupported format: ${format as string}`);
}

/** @deprecated 请使用 {@link downloadCosResource}；保留别名兼容旧命名 */
export const downloadCosHtml = downloadCosResource;

async function downloadAsHtml(
  fetchUrl: string,
  fileName: string,
  watermark: IDownloadCosResourceOptions['watermark'],
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(fetchUrl, { signal });
  if (!response.ok) {
    throw new Error(`[htmlExport] fetch html failed: ${response.status}`);
  }
  const htmlContent = await response.text();
  throwIfAborted(signal);

  const inject = watermark?.injectIntoHtml !== false;
  const wmUrl = inject ? resolveWatermarkDataUrl(watermark) : '';
  const finalHtml = wmUrl
    ? injectWatermarkIntoHtml(htmlContent, wmUrl)
    : htmlContent;

  const blob = new Blob([finalHtml], { type: 'text/html;charset=utf-8' });
  downloadFile(blob, fileName);
}

async function downloadAsBlob(
  fetchUrl: string,
  fileName: string,
  mimeType: string,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(fetchUrl, { signal });
  if (!response.ok) {
    throw new Error(`[htmlExport] fetch blob failed: ${response.status}`);
  }
  const raw = await response.blob();
  throwIfAborted(signal);

  const blob =
    raw.type && raw.type !== 'application/octet-stream'
      ? raw
      : new Blob([raw], { type: mimeType });
  downloadFile(blob, fileName);
}

async function downloadAsPdf(
  fetchUrl: string,
  fileName: string,
  watermark: IDownloadCosResourceOptions['watermark'],
  pdfOptions: ICosExportPdfOptions | undefined,
  signal?: AbortSignal,
): Promise<void> {
  const cfg = { ...DEFAULT_PDF, ...pdfOptions };
  if (typeof document === 'undefined' || !document.body) {
    throw new Error('[htmlExport] document.body is not available');
  }

  const iframe = document.createElement('iframe');
  iframe.style.cssText = [
    'position:fixed',
    'left:0',
    'top:0',
    `width:${cfg.viewportWidth}px`,
    'height:1px',
    'border:0',
    'opacity:0',
    'pointer-events:none',
    'z-index:-1',
  ].join(';');
  iframe.src = fetchUrl;
  document.body.appendChild(iframe);

  try {
    await waitForIframeLoad(iframe, cfg.iframeLoadTimeoutMs, signal);
    throwIfAborted(signal);

    const iframeWin = iframe.contentWindow;
    const iframeDoc = iframe.contentDocument || iframeWin?.document;
    if (!iframeDoc || !iframeWin) {
      throw new Error('[htmlExport] iframe document is not accessible');
    }

    const htmlEl = iframeDoc.documentElement;
    const bodyEl = iframeDoc.body;
    if (!bodyEl) {
      throw new Error('[htmlExport] iframe body is empty');
    }

    bodyEl.style.margin = '0';
    bodyEl.style.padding = '0';
    htmlEl.style.margin = '0';
    htmlEl.style.padding = '0';

    const contentRoot = resolveContentRoot(bodyEl);

    // 先撑开再量尺寸，避免 scrollHeight 在压缩状态下不准
    iframe.style.width = `${cfg.viewportWidth}px`;
    iframe.style.height = '10000px';
    await nextAnimationFrame();
    throwIfAborted(signal);

    const contentW = contentRoot.scrollWidth || bodyEl.scrollWidth || cfg.viewportWidth;
    const contentH = bodyEl.scrollHeight || 1;

    iframe.style.width = `${contentW}px`;
    iframe.style.height = `${contentH}px`;
    await nextAnimationFrame();
    throwIfAborted(signal);

    await waitForImages(iframeDoc, cfg.imageLoadTimeoutMs, signal);
    throwIfAborted(signal);

    const rawCanvas = await html2canvas(contentRoot, {
      scale: cfg.scale,
      useCORS: true,
      backgroundColor: null,
      windowWidth: contentW,
      windowHeight: contentH,
      width: contentW,
      height: contentH,
      scrollX: 0,
      scrollY: 0,
    });
    throwIfAborted(signal);

    const applyWm = watermark?.applyToPdf !== false;
    const wmUrl = applyWm ? resolveWatermarkDataUrl(watermark) : '';
    const canvas = wmUrl
      ? await applyWatermarkToCanvas(rawCanvas, wmUrl)
      : rawCanvas;
    throwIfAborted(signal);

    if (cfg.pageFormat === 'auto') {
      saveCanvasAsSinglePagePdf(canvas, fileName, cfg.jpegQuality);
    } else {
      saveCanvasAsA4Pdf(canvas, fileName, cfg.jpegQuality);
    }
  } finally {
    if (iframe.parentNode) {
      document.body.removeChild(iframe);
    }
  }
}

/** 单页长图 PDF：宽度固定 A4 宽，高度按内容比例 */
function saveCanvasAsSinglePagePdf(
  canvas: HTMLCanvasElement,
  fileName: string,
  jpegQuality: number,
): void {
  if (!canvas.width || !canvas.height) {
    throw new Error('[htmlExport] canvas is empty, cannot generate pdf');
  }
  const pdfW = A4_W_MM;
  const pdfH = Math.max((canvas.height / canvas.width) * pdfW, 1);
  const pdf = new JsPDF('p', 'mm', [pdfW, pdfH]);
  const imgData = canvas.toDataURL('image/jpeg', jpegQuality);
  pdf.addImage(imgData, 'JPEG', 0, 0, pdfW, pdfH);
  pdf.save(fileName);
}

/** 按 A4 高度切片多页 PDF */
function saveCanvasAsA4Pdf(
  canvas: HTMLCanvasElement,
  fileName: string,
  jpegQuality: number,
): void {
  if (!canvas.width || !canvas.height) {
    throw new Error('[htmlExport] canvas is empty, cannot generate pdf');
  }

  const pxPerMm = canvas.width / A4_W_MM;
  const pageHeightPx = A4_H_MM * pxPerMm;
  const totalPages = Math.ceil(canvas.height / pageHeightPx) || 1;
  const pdf = new JsPDF('p', 'mm', 'a4');

  for (let page = 0;page < totalPages;page += 1) {
    if (page > 0) {
      pdf.addPage();
    }

    const srcY = page * pageHeightPx;
    const srcH = Math.min(pageHeightPx, canvas.height - srcY);

    const pageCanvas = document.createElement('canvas');
    pageCanvas.width = canvas.width;
    pageCanvas.height = srcH;
    const ctx = pageCanvas.getContext('2d');
    if (!ctx) {
      throw new Error('[htmlExport] failed to create page canvas context');
    }
    ctx.drawImage(
      canvas,
      0,
      srcY,
      canvas.width,
      srcH,
      0,
      0,
      canvas.width,
      srcH,
    );

    const imgData = pageCanvas.toDataURL('image/jpeg', jpegQuality);
    const imgH = srcH / pxPerMm;
    pdf.addImage(imgData, 'JPEG', 0, 0, A4_W_MM, imgH);
  }

  pdf.save(fileName);
}
